import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Components
import ModalContainer from '../../containers/ModalContainer';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';

// Styles
import {
  ModalContent,
  ScrollableContent,
  Row,
  AddRow,
  StyledInputText,
  SectionTitle,
  MembersTableContainer,
  EmailTd,
  InactiveTag,
  EmptyMessage,
  OtherGroupList,
  Footer,
  ActionButton,
} from './styles';
// La tabla y la papelera de siempre (las de las tablas de Gestión).
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../../display/tables/summaryTables/SummaryTablesStyles';
import {
  ActionButton as RowIconButton,
  ActionCell,
  ActionIcon,
} from '../../../display/tables/formTables/managementTables/ExternalCompaniesManagementTable/styles';
import RemoveIcon from '../../../../assets/icons/remove-icon.svg';

// Types
import { ModalProps } from './types';
import { UserGroup } from '../../../../pages/main/management/groups/types';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';

// GraphQL
import { client } from '../../../../gql/client';
import {
  RENAME_USER_GROUP,
  ADD_USER_GROUP_MEMBERS,
  REMOVE_USER_GROUP_MEMBERS,
} from '../../../../gql/mutations/userGroups';

// Utils
import { showSuccessToast, showBackendErrorToast } from '../../../../utils/toasts';

type GroupMutationName =
  | 'renameUserGroup'
  | 'addUserGroupMembers'
  | 'removeUserGroupMembers';

const fullName = (u: { name?: string | null; surName?: string | null }) =>
  `${u.name ?? ''} ${u.surName ?? ''}`.trim();

export const EditUserGroupModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['management', 'common', 'tables']);
  const theme = useTheme();
  const dropdownRef = useRef<ImperativeRefMultiSelect>(null);

  // Copia local: se refresca con lo que devuelve cada mutation y el modal
  // sigue abierto con los datos nuevos.
  const [group, setGroup] = useState<UserGroup>(data.group);
  const [name, setName] = useState(data.group.name);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBusy, setIsBusy] = useState(false);

  // Quién está ya en OTRO grupo (nombre del grupo) para no ofrecerlo.
  const otherGroupByUser = useMemo(() => {
    const map = new Map<string, string>();
    data.groups
      .filter((g) => g.id !== group.id)
      .forEach((g) => g.members.forEach((m) => map.set(m.id, g.name)));
    return map;
  }, [data.groups, group.id]);

  const memberIds = useMemo(
    () => new Set(group.members.map((m) => m.id)),
    [group.members]
  );

  // Candidatos: fuera los inactivos (no pueden entrar, así que no usan
  // banderitas) y ordenados por nombre (a igual nombre, por correo).
  const candidates = useMemo(
    () =>
      data.appUsers
        .filter((u) => u.isActive !== false && !memberIds.has(u.id))
        .sort(
          (x, y) =>
            fullName(x).localeCompare(fullName(y)) ||
            (x.email ?? '').localeCompare(y.email ?? '')
        ),
    [data.appUsers, memberIds]
  );
  const selectableUsers = candidates.filter((u) => !otherGroupByUser.has(u.id));
  const blockedUsers = candidates.filter((u) => otherGroupByUser.has(u.id));

  // «Nombre Apellido · correo»: el buscador encuentra también por correo y
  // distingue a dos personas que se llaman igual.
  const options = selectableUsers.map((u) => ({
    value: u.id,
    label: u.email ? `${fullName(u)} · ${u.email}` : fullName(u),
  }));

  /** Lanza una mutation del grupo, avisa y actualiza la copia local. */
  const runMutation = async (
    mutation: typeof RENAME_USER_GROUP,
    field: GroupMutationName,
    variables: Record<string, unknown>,
    successMessage: string
  ): Promise<boolean> => {
    setIsBusy(true);
    try {
      const result = await client.mutate({ mutation, variables });
      const updated = result.data?.[field] as UserGroup | undefined;
      if (updated) {
        setGroup(updated);
        setName(updated.name);
      }
      showSuccessToast(successMessage);
      data.onSuccess?.();
      return true;
    } catch (err) {
      showBackendErrorToast(err, t('management:groups.saveError'));
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  const trimmedName = name.trim();
  const canRename = !isBusy && trimmedName !== '' && trimmedName !== group.name;

  const handleRename = () =>
    runMutation(
      RENAME_USER_GROUP,
      'renameUserGroup',
      { id: group.id, name: trimmedName },
      t('management:groups.renamed')
    );

  const handleAdd = async () => {
    const ok = await runMutation(
      ADD_USER_GROUP_MEMBERS,
      'addUserGroupMembers',
      { id: group.id, userIds: selectedIds },
      t('management:groups.membersAdded')
    );
    if (ok) {
      setSelectedIds([]);
      dropdownRef.current?.clearSelected();
    }
  };

  const handleRemove = (userId: string) =>
    runMutation(
      REMOVE_USER_GROUP_MEMBERS,
      'removeUserGroupMembers',
      { id: group.id, userIds: [userId] },
      t('management:groups.memberRemoved')
    );

  return (
    <ModalContainer
      width={60}
      onClickClose={onClickClose}
      fill={theme.colors.black}
      title={t('management:groups.editTitle')}
    >
      <ModalContent {...props}>
        <ScrollableContent>
          <Row>
            <StyledInputText
              label={t('common:labels.name')}
              labelSize="S"
              typeInput="normal"
              placeholder={t('management:groups.namePlaceholder')}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
            />
            <ActionButton
              type="button"
              text={t('common:buttons.save')}
              onClick={handleRename}
              disabled={!canRename}
            />
          </Row>

          <SectionTitle variant="semiBold">
            {t('management:groups.members')}
            {group.members.length > 0 && ` (${group.members.length})`}
          </SectionTitle>
          <MembersTableContainer data-testid="edit-user-group-members">
            <Table>
              <Thead>
                <tr>
                  <Th>{t('tables:headers.name')}</Th>
                  <Th>{t('common:labels.email')}</Th>
                  <Th aria-label={t('tables:headers.actions')} />
                </tr>
              </Thead>
              <Tbody>
                {group.members.length === 0 ? (
                  <tr>
                    <Td colSpan={3}>
                      <EmptyMessage size="S">
                        {t('management:groups.noMembers')}
                      </EmptyMessage>
                    </Td>
                  </tr>
                ) : (
                  group.members.map((member) => (
                    <Tr key={member.id}>
                      <Td>
                        {member.name} {member.surName}
                        {!member.isActive && (
                          <InactiveTag>{t('management:groups.inactive')}</InactiveTag>
                        )}
                      </Td>
                      <EmailTd title={member.email}>{member.email}</EmailTd>
                      <Td>
                        <ActionCell>
                          <RowIconButton
                            type="button"
                            title={t('management:groups.removeMember')}
                            disabled={isBusy}
                            onClick={() => handleRemove(member.id)}
                          >
                            <ActionIcon
                              src={RemoveIcon}
                              alt={t('management:groups.removeMember')}
                            />
                          </RowIconButton>
                        </ActionCell>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </MembersTableContainer>

          <AddRow>
            <MultiSelectDropdown
              ref={dropdownRef}
              inputSize="M"
              typeDropDown="Default"
              placeholder={t('management:groups.addMembers')}
              options={options}
              selectedValues={selectedIds}
              onChangeSelected={setSelectedIds}
              searchable
              searchPlaceholder={t('management:groups.searchUsers')}
              isDisabled={isBusy}
            />
            <ActionButton
              type="button"
              text={t('management:groups.add')}
              onClick={handleAdd}
              disabled={isBusy || selectedIds.length === 0}
            />
          </AddRow>

          {blockedUsers.length > 0 && (
            <OtherGroupList>
              {blockedUsers.map((u) => (
                <li key={u.id}>
                  {u.name} {u.surName}{' '}
                  {t('management:groups.inOtherGroup', {
                    group: otherGroupByUser.get(u.id),
                  })}
                </li>
              ))}
            </OtherGroupList>
          )}
        </ScrollableContent>

        <Footer>
          <ActionButton
            type="button"
            variant="secondary"
            text={t('common:buttons.cancel')}
            onClick={onClickClose}
          />
        </Footer>
      </ModalContent>
    </ModalContainer>
  );
};
